import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type Props = {
  workspaceId: string;
  columnCap: number;
  onCreated: () => void | Promise<void>;
};

export function StartTopicTreeCard({ workspaceId, columnCap, onCreated }: Props) {
  const initial = Math.min(3, columnCap);
  const [count, setCount] = useState<number>(initial);
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    setCreating(true);
    const { error } = await supabase.from("content_frameworks").insert({
      workspace_id: workspaceId,
      name: "My Topic Tree",
      is_active: true,
      column_count_target: Math.min(Math.max(count, 1), columnCap),
    });
    setCreating(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    await onCreated();
  };

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle>Start your Topic Tree</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Choose how many columns to create. You can name each column and add topic chips next — those topic names become the chips you can tag Corpus documents with.
        </p>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">Columns</span>
          <Select value={String(count)} onValueChange={(value) => setCount(Number(value))} disabled={creating}>
            <SelectTrigger className="h-8 w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: columnCap }, (_, idx) => idx + 1).map((n) => (
                <SelectItem key={n} value={String(n)}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground">cap {columnCap}</span>
          <Button size="sm" onClick={() => void handleCreate()} disabled={creating}>
            Create Topic Tree
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
